import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import ReconAddons from "@/components/ReconAddons";
import { ButtonPrimary, ButtonSecondary } from "@/components/Buttons";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { ShoppingBag, Trash2, FlaskConical, Truck } from "lucide-react";

const priceNumber = (raw) => {
    const n = parseFloat(String(raw || "").replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) ? n : 0;
};

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function Cart() {
    const { user, openAuth } = useAuth();
    const { items, remove, setQty } = useCart();
    const navigate = useNavigate();
    const [thresholdCents, setThresholdCents] = useState(0);

    useEffect(() => {
        api.get("/checkout/config")
            .then((res) => setThresholdCents(res.data.free_shipping_threshold_cents || 0))
            .catch(() => setThresholdCents(0));
    }, []);

    const subtotalCents = useMemo(
        () => Math.round(items.reduce((sum, i) => sum + priceNumber(i.price) * (i.qty || 1), 0) * 100),
        [items]
    );

    const freeShipRemaining = Math.max(thresholdCents - subtotalCents, 0);
    const freeShipPct = thresholdCents ? Math.min((subtotalCents / thresholdCents) * 100, 100) : 0;

    const handleCheckout = () => {
        if (!user) {
            openAuth("login", () => navigate("/checkout"));
            return;
        }
        if (!user.email_verified) {
            openAuth("verify");
            return;
        }
        navigate("/checkout");
    };

    return (
        <div data-testid="cart-page">
            <PageHeader
                eyebrow="Your Cart"
                title="Research Material Cart"
                description="Review your selected research materials, then place your order. Payment is always a separate step."
            />

            <section className="pb-24 sm:pb-32 px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    {items.length === 0 ? (
                        <Reveal>
                            <div className="border border-dashed border-gold/30 rounded-sm py-20 px-8 text-center bg-white">
                                <ShoppingBag className="h-10 w-10 text-gold/50 mx-auto" strokeWidth={1.25} />
                                <h2 className="mt-6 font-serif text-2xl font-light text-brand">Your cart is empty</h2>
                                <p className="mt-4 text-sm leading-relaxed text-mist max-w-md mx-auto">
                                    Browse the research-material catalog and add materials to your cart.
                                </p>
                                <div className="mt-8">
                                    <ButtonSecondary to="/materials" data-testid="cart-browse-btn">
                                        Browse Research Materials
                                    </ButtonSecondary>
                                </div>
                            </div>
                        </Reveal>
                    ) : (
                        <>
                            <div className="divide-y divide-stone-200 border-y border-stone-200" data-testid="cart-items">
                                {items.map((item) => (
                                    <Reveal key={item.key}>
                                        <div
                                            className="py-6 flex items-center gap-5"
                                            data-testid={`cart-item-${item.key.replace(/[^a-z0-9]+/gi, "-")}`}
                                        >
                                            <span className="h-14 w-14 shrink-0 bg-surface2 border border-stone-200 rounded-sm flex items-center justify-center">
                                                <FlaskConical className="h-6 w-6 text-gold/50" strokeWidth={1.25} />
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <Link
                                                    to={`/materials/${item.id}`}
                                                    className="font-serif text-lg text-brand hover:text-wine transition-colors duration-300"
                                                >
                                                    {item.name}
                                                </Link>
                                                <p className="mt-1 text-xs text-mist">
                                                    {item.category}
                                                    {item.variant_label && ` · ${item.variant_label}`}
                                                    {item.is_placeholder && " · Placeholder entry"}
                                                </p>
                                                {(item.sku || item.lot_batch) && (
                                                    <p className="mt-0.5 text-[0.6rem] text-slate-400">
                                                        {[item.sku && `SKU ${item.sku}`, item.lot_batch && `Lot ${item.lot_batch}`]
                                                            .filter(Boolean)
                                                            .join(" · ")}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0" data-testid={`cart-qty-${item.key.replace(/[^a-z0-9]+/gi, "-")}`}>
                                                <button
                                                    type="button"
                                                    aria-label="Decrease quantity"
                                                    onClick={() => setQty(item.key, (item.qty || 1) - 1)}
                                                    className="h-7 w-7 border border-stone-200 text-slate-500 hover:border-gold/60 hover:text-wine transition-colors duration-200 text-sm"
                                                >
                                                    −
                                                </button>
                                                <span className="w-6 text-center text-sm text-slate-700">{item.qty || 1}</span>
                                                <button
                                                    type="button"
                                                    aria-label="Increase quantity"
                                                    onClick={() => setQty(item.key, (item.qty || 1) + 1)}
                                                    className="h-7 w-7 border border-stone-200 text-slate-500 hover:border-gold/60 hover:text-wine transition-colors duration-200 text-sm"
                                                >
                                                    +
                                                </button>
                                            </div>
                                            <span className="text-sm text-slate-700 shrink-0 w-20 text-right">
                                                {item.price ? item.price : "Price TBA"}
                                            </span>
                                            <button
                                                type="button"
                                                aria-label={`Remove ${item.name}`}
                                                data-testid={`cart-remove-${item.key.replace(/[^a-z0-9]+/gi, "-")}`}
                                                onClick={() => remove(item.key)}
                                                className="text-slate-400 hover:text-red-600 transition-colors duration-300 shrink-0"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </Reveal>
                                ))}
                            </div>

                            <Reveal className="mt-10">
                                <ReconAddons className="border border-stone-200 bg-[#FDFAF5] p-7 sm:p-8" />
                            </Reveal>

                            {thresholdCents > 0 && (
                                <Reveal className="mt-8">
                                    <div className="border border-stone-200 bg-white px-6 py-5" data-testid="cart-free-shipping-hint">
                                        <div className="flex items-center gap-3">
                                            <Truck className="h-4 w-4 text-gold shrink-0" strokeWidth={1.5} />
                                            {freeShipRemaining > 0 ? (
                                                <p className="text-xs text-slate-600" data-testid="cart-free-shipping-text">
                                                    Add <span className="font-semibold text-wine">{money(freeShipRemaining)}</span> more
                                                    to qualify for <span className="font-semibold">free shipping</span> on
                                                    orders over {money(thresholdCents)}.
                                                </p>
                                            ) : (
                                                <p className="text-xs text-slate-600" data-testid="cart-free-shipping-text">
                                                    <span className="font-semibold text-wine">Free shipping unlocked</span> — your
                                                    order qualifies for free shipping.
                                                </p>
                                            )}
                                        </div>
                                        <div className="mt-3 h-1 bg-stone-100 overflow-hidden rounded-full">
                                            <div
                                                className="h-1 bg-gold rounded-full transition-[width] duration-500 ease-out"
                                                style={{ width: `${freeShipPct}%` }}
                                                data-testid="cart-free-shipping-progress"
                                            />
                                        </div>
                                    </div>
                                </Reveal>
                            )}

                            <Reveal className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-6">
                                <p className="text-xs leading-relaxed text-slate-500 max-w-md">
                                    All materials are supplied strictly for laboratory research use only. Not for
                                    human or veterinary use.
                                </p>
                                <ButtonPrimary onClick={handleCheckout} data-testid="cart-checkout-btn">
                                    Proceed to Checkout
                                </ButtonPrimary>
                            </Reveal>
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}
